/* eslint-disable @angular-eslint/component-max-inline-declarations */

/* eslint-disable @angular-eslint/no-output-on-prefix */
/* eslint-disable @angular-eslint/component-class-suffix */
/* eslint-disable @typescript-eslint/no-restricted-imports */

import {
  AfterContentInit,
  AfterViewInit,
  booleanAttribute,
  Component,
  computed,
  ContentChildren,
  ElementRef,
  EventEmitter,
  input,
  Input,
  numberAttribute,
  OnDestroy,
  Output,
  signal,
  ViewChild,
} from "@angular/core";
import { PrimeTemplate } from "primeng/api";
import { BaseComponent } from "primeng/basecomponent";
import { Button as PrimeNGButton } from "primeng/button";
import { ClickListenerProvider } from "./click-listener-provider";

type ButtonInterface = Omit<PrimeNGButton, `_${string}` | `${string}Template` | keyof BaseComponent>;
@Component({
  selector: "new-ui-button",
  imports: [PrimeNGButton],
  template: `
    <p-button
      [dt]="dt"
      [type]="type"
      [iconPos]="iconPos"
      [icon]="icon"
      [badge]="badge"
      [label]="label"
      [disabled]="disabled"
      [loading]="loading"
      [loadingIcon]="loadingIcon"
      [raised]="raised"
      [rounded]="rounded"
      [text]="text"
      [plain]="plain"
      [severity]="severity"
      [outlined]="outlined"
      [link]="link"
      [tabindex]="tabindex"
      [size]="size"
      [variant]="variant"
      [style]="style"
      [styleClass]="styleClass"
      [badgeClass]="badgeClass"
      [badgeSeverity]="badgeSeverity"
      [ariaLabel]="ariaLabel"
      [autofocus]="autofocus"
      [fluid]="fluid"
      [attr.data-testid]="computedTestId()"
      [class]="stylePButtonClass ?? ''"
      (onClick)="onClick.emit($event)"
      (onFocus)="onFocus.emit($event)"
      (onBlur)="onBlur.emit($event)"
    >
      <ng-content></ng-content>
    </p-button>
  `,
})
export class NewUiButton implements ButtonInterface, AfterContentInit, AfterViewInit, OnDestroy {
  private readonly listenerProvider = new ClickListenerProvider("ButtonComponent");

  constructor(private readonly hostElement: ElementRef) {}

  get nativeElement(): HTMLElement {
    return this.el.nativeElement;
  }

  @Input() dt: PrimeNGButton["dt"];
  @Input() type: PrimeNGButton["type"] = "button";
  @Input() iconPos: PrimeNGButton["iconPos"] = "left";
  @Input() icon: PrimeNGButton["icon"];
  @Input() badge: PrimeNGButton["badge"];
  @Input() label: PrimeNGButton["label"];
  @Input({ transform: booleanAttribute }) disabled: PrimeNGButton["disabled"];
  @Input({ transform: booleanAttribute }) loading: PrimeNGButton["loading"] = false;
  @Input() loadingIcon: PrimeNGButton["loadingIcon"];
  @Input({ transform: booleanAttribute }) raised: PrimeNGButton["raised"] = false;
  @Input({ transform: booleanAttribute }) rounded: PrimeNGButton["rounded"] = false;
  @Input({ transform: booleanAttribute }) text: PrimeNGButton["text"] = false;
  @Input({ transform: booleanAttribute }) plain: PrimeNGButton["plain"] = false;
  @Input() severity: PrimeNGButton["severity"];
  @Input({ transform: booleanAttribute }) outlined: PrimeNGButton["outlined"] = false;
  @Input({ transform: booleanAttribute }) link: PrimeNGButton["link"] = false;
  @Input({ transform: numberAttribute }) tabindex: PrimeNGButton["tabindex"];
  @Input() size: PrimeNGButton["size"];
  @Input() variant: PrimeNGButton["variant"];
  @Input() style: PrimeNGButton["style"];
  @Input() styleClass: PrimeNGButton["styleClass"];
  @Input() badgeClass: PrimeNGButton["badgeClass"];
  @Input() badgeSeverity: PrimeNGButton["badgeSeverity"];
  @Input() ariaLabel: PrimeNGButton["ariaLabel"];
  @Input() autofocus: PrimeNGButton["autofocus"];
  @Input() fluid: PrimeNGButton["fluid"];
  @Input() stylePButtonClass: string | undefined;

  readonly testId = input<string>();
  readonly rawTestId = signal<string>(null);
  readonly computedTestId = computed(() => {
    return this.testId() || this.rawTestId();
  });

  @Output() readonly onClick: PrimeNGButton["onClick"] = new EventEmitter<MouseEvent>();
  @Output() readonly onFocus: PrimeNGButton["onFocus"] = new EventEmitter<FocusEvent>();
  @Output() readonly onBlur: PrimeNGButton["onBlur"] = new EventEmitter<FocusEvent>();
  @ContentChildren(PrimeTemplate) templates: PrimeNGButton["templates"];

  @ViewChild(PrimeNGButton, { static: true })
  button: PrimeNGButton;

  get buttonClass(): PrimeNGButton["buttonClass"] {
    return this.button.buttonClass;
  }

  get el(): PrimeNGButton["el"] {
    return this.button.el;
  }

  get buttonProps(): PrimeNGButton["buttonProps"] {
    return this.button.buttonProps;
  }

  set buttonProps(val: PrimeNGButton["buttonProps"]) {
    this.button.buttonProps = val;
  }

  get hasFluid(): PrimeNGButton["hasFluid"] {
    return this.button.hasFluid;
  }

  ngAfterContentInit(): void {
    this.button.templates = this.templates;

    this.templates?.forEach((item) => {
      switch (item.getType()) {
        case "content":
          this.button.contentTemplate = item.template;
          break;
        case "icon":
          this.button.iconTemplate = item.template;
          break;
        case "loadingicon":
          this.button.loadingIconTemplate = item.template;
          break;
        default:
          this.button.contentTemplate = item.template;
          break;
      }
    });
  }

  ngAfterViewInit(): void {
    // Fallback if @Input() testId wasn't set
    const rawTestId = this.hostElement.nativeElement.getAttribute("data-testid");
    if (rawTestId) {
      this.rawTestId.set(rawTestId);
    }

    // Pass the value directly to the underlying p-button
    if (this.computedTestId() && this.el?.nativeElement) {
      this.el.nativeElement.setAttribute("data-testid", this.computedTestId());
    }
    this.hostElement.nativeElement.removeAttribute("data-testid");
    this.nativeElement.addEventListener("click", this.listenerProvider.listener, true);
  }

  ngOnDestroy(): void {
    this.nativeElement.removeEventListener("click", this.listenerProvider.listener, true);
  }

  spinnerIconClass(): ReturnType<PrimeNGButton["spinnerIconClass"]> {
    return this.button.spinnerIconClass();
  }

  iconClass(): ReturnType<PrimeNGButton["iconClass"]> {
    return this.button.iconClass();
  }
}
